import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Stack,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useUserManagement } from '../context/UserManagementContext';
import { userManagementService, User } from '../services/userManagementService';

interface UserDisableModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (user: User, disabled: boolean) => Promise<{ success: boolean; message: string }>;
}

export function UserDisableModal({ open, onClose, onConfirm }: UserDisableModalProps) {
  const { state } = useUserManagement();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDisabling = state.userToDisable ? !state.userToDisable.disabled : false;

  const handleConfirm = async () => {
    if (!state.userToDisable) return;

    setLoading(true);
    setError(null);

    try {
      const result = await onConfirm(state.userToDisable, isDisabling);
      if (result.success) {
        onClose();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to ${isDisabling ? 'disable' : 'enable'} user`);
    } finally {
      setLoading(false);
    }
  };

  const getUserDisplayName = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) {
      return user.firstName;
    }
    if (user.lastName) {
      return user.lastName;
    }
    return user.email;
  };

  const getUserInitials = (user: User) => {
    const firstName = user.firstName || '';
    const lastName = user.lastName || '';
    
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    if (firstName) {
      return firstName.charAt(0).toUpperCase();
    }
    if (lastName) {
      return lastName.charAt(0).toUpperCase();
    }
    return user.email.charAt(0).toUpperCase();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!state.userToDisable) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              p: 1,
              borderRadius: '50%',
              bgcolor: isDisabling ? 'warning.light' : 'success.light',
              color: isDisabling ? 'warning.contrastText' : 'success.contrastText',
            }}
          >
            {isDisabling ? <BlockIcon /> : <CheckCircleIcon />}
          </Box>
          <Typography variant="h6" component="div">
            {isDisabling ? 'Disable User' : 'Enable User'}
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {error && (
            <Alert severity="error">{error}</Alert>
          )}

          <Alert 
            severity={isDisabling ? 'warning' : 'info'} 
            icon={isDisabling ? <BlockIcon /> : <CheckCircleIcon />}
          >
            <Typography variant="body2">
              {isDisabling ? (
                <>
                  <strong>Disable User:</strong> This will prevent the user from logging in and accessing the system. 
                  Their data will be preserved and they can be re-enabled later.
                </>
              ) : (
                <>
                  <strong>Enable User:</strong> This will restore the user's access to the system. 
                  They will be able to log in and use their account normally.
                </>
              )}
            </Typography>
          </Alert>

          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {isDisabling ? 'You are about to disable the following user:' : 'You are about to enable the following user:'}
            </Typography>
            
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                bgcolor: 'grey.50',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  src={state.userToDisable.avatar || undefined}
                  sx={{
                    bgcolor: userManagementService.getRoleColor(state.userToDisable.role),
                    width: 50,
                    height: 50,
                    opacity: state.userToDisable.disabled ? 0.6 : 1,
                  }}
                >
                  {getUserInitials(state.userToDisable)}
                </Avatar>
                
                <Box flex={1}>
                  <Typography variant="subtitle1" fontWeight="medium">
                    {getUserDisplayName(state.userToDisable)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {state.userToDisable.email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Role: {userManagementService.getRoleLabel(state.userToDisable.role)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Member since: {formatDate(state.userToDisable.createdAt)}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {isDisabling ? 'What will happen when disabled:' : 'What will happen when enabled:'}
            </Typography>
            <Stack spacing={1}>
              {isDisabling ? (
                <>
                  <Typography variant="body2" color="text.secondary">
                    • User will not be able to log in to the system
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • All existing data and posts will be preserved
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • User will receive an error when attempting to log in
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • User can be re-enabled at any time by an admin
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    • User will be able to log in to the system
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • All existing data and posts will be accessible
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • User will have full access based on their role
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • User can be disabled again if needed
                  </Typography>
                </>
              )}
            </Stack>
          </Box>

          {isDisabling && (
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Note:</strong> Disabled users will see a "Account Disabled" message when they try to log in. 
                They should contact an administrator for assistance.
              </Typography>
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading}
          variant="contained"
          color={isDisabling ? 'warning' : 'success'}
          startIcon={loading ? <CircularProgress size={20} /> : (isDisabling ? <BlockIcon /> : <CheckCircleIcon />)}
        >
          {loading 
            ? (isDisabling ? 'Disabling...' : 'Enabling...') 
            : (isDisabling ? 'Disable User' : 'Enable User')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
}
